import React, { useState } from 'react';
import { Modal } from '@heartinternet/hi-ui';
import { Text } from '@heartinternet/hi-ui';
import { Select } from '@heartinternet/hi-ui';
import { Input } from '@heartinternet/hi-ui';
import { Button } from '@heartinternet/hi-ui';
import { Flex } from '@heartinternet/hi-ui';
import type { Package } from '../types';

const GROUPS_UPDATE_ENDPOINT = '/manage/package-groups-update.cgi';

interface AddToGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedPackages: Package[];
  groups: string[];
  onGroupChanged: () => void;
}

export const AddToGroupModal: React.FC<AddToGroupModalProps> = ({
  isOpen,
  onClose,
  selectedPackages,
  groups,
  onGroupChanged,
}) => {
  const [selectedGroup, setSelectedGroup] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const sanitiseGroupName = (name: string) => name.replace(/[/\\&#?<>]+/g, ' ').trim();

  const handleAssign = async (groupName: string, isNew: boolean) => {
    setLoading(true);
    setError(null);

    try {
      const formData = new URLSearchParams();
      if (isNew) {
        formData.append('add-group', groupName);
      }
      selectedPackages.forEach((p) => {
        formData.append('add-member', `${p.login}:${groupName}`);
      });
      await fetch(GROUPS_UPDATE_ENDPOINT, { method: 'POST', body: formData });
      onGroupChanged();
      handleClose();
    } catch {
      setError('Failed to assign group.');
    }
    setLoading(false);
  };

  const handleSelectChange = (value: string) => {
    if (value === '__new__') {
      setIsCreating(true);
      setSelectedGroup('');
    } else {
      setSelectedGroup(value);
      setIsCreating(false);
    }
  };

  const handleConfirm = () => {
    if (isCreating) {
      handleAssign(sanitiseGroupName(newGroupName), true);
    } else {
      handleAssign(selectedGroup, false);
    }
  };

  const handleClose = () => {
    setSelectedGroup('');
    setNewGroupName('');
    setIsCreating(false);
    setError(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Group"
      size="small"
      footer={
        <Button
          variant="primary"
          size="small"
          onClick={handleConfirm}
          disabled={
            loading || (!selectedGroup && !isCreating) || (isCreating && !newGroupName.trim())
          }
          loading={loading}
        >
          {isCreating ? 'Create & Assign' : 'Group'}
        </Button>
      }
    >
      <Flex direction="column" gap="md">
        <Text variant="body" inline>
          Groups let you organise your hosting packages for easier management.
        </Text>
        <Text variant="body" inline>
          Add {selectedPackages.length} package{selectedPackages.length > 1 ? 's' : ''} to a group.
        </Text>

        {error && <Text variant="error">{error}</Text>}

        <Select
          value={isCreating ? '__new__' : selectedGroup}
          onChange={(e) => handleSelectChange(e.target.value)}
          size="sm"
          fullWidth
          aria-label="Select group"
        >
          <option value="">Select a group</option>
          {groups.map((g) => (
            <option key={g} value={g}>
              {g}
            </option>
          ))}
          <option value="__new__">+ Create new group...</option>
        </Select>

        {isCreating && (
          <Input
            type="text"
            value={newGroupName}
            onChange={(e) => setNewGroupName(e.target.value)}
            size="sm"
            placeholder="New group name"
            fullWidth
          />
        )}
      </Flex>
    </Modal>
  );
};
